import { useMemo, useState } from "react";
import { StyleSheet, Text, useWindowDimensions, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  formatDraftWeekParts,
  MAX_CYCLE_WEEKS,
  modelBlockFits,
  programRoundLabel,
  PROGRAM_FOCUS,
  TETRAD_ROUND_TYPES,
  type Draft,
  type DraftLineOptions,
  type NextBlockResult,
  type ProgramFocus,
} from "@heraion/shared";

import { useProgramModel } from "../Library/programModelSource";
import { ModelFocusRow } from "../ProgramComposer/ModelFocusRow";
import { PageSheet } from "../primitives/PageSheet";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import type { UiTheme } from "../theme";
import { NEXT_BLOCK_ROUNDS, type BlockFocus } from "./useV3ProgressionActions";
import type { V3Styles } from "./v3Styles";

/** How many exercises the preview shows. */
const PREVIEW_ROWS = 4;

/** Under this width, or over this text size, a preview row puts the
 *  prescription under the name instead of beside it. */
const NARROW = 360;
const LARGE_TEXT = 1.25;

// Flow 9 (#336 slice 5, brief §5.4b): «Add W5-W8». What the next tetrad is,
// how load, reps and effort rows carry on, and the first new round of the
// open day's first exercises, as addNextBlock writes it. #356: on a program
// a model wrote, the wizard's focus chips; another focus takes the model's
// rows in the new rounds. The model is read when the sheet opens; until then,
// or without one, the sheet is as before.
export function NextBlockSheet({
  theme,
  styles,
  draft,
  sIdx,
  lineOptions,
  resolveName,
  apiFetch,
  nextBlock,
  onAdd,
  onClose,
}: {
  theme: UiTheme;
  styles: V3Styles;
  draft: Draft;
  sIdx: number;
  lineOptions: DraftLineOptions;
  resolveName: (id: string, snapshot?: string) => string;
  apiFetch?: ProgramApiFetch;
  nextBlock: (draft: Draft, change?: BlockFocus) => NextBlockResult;
  onAdd: (change?: BlockFocus) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const local = useMemo(() => makeStyles(theme), [theme]);
  const { width, fontScale } = useWindowDimensions();
  const stacked = width < NARROW || fontScale > LARGE_TEXT;
  const first = draft.cycleWeeks;
  const origin = draft.origin;
  // #356: the one reader of models (network, else the device copy). Without
  // an origin or an api nothing is read: no chips, and no room kept.
  const modelId = origin?.modelId;
  const { model, loading } = useProgramModel(modelId, apiFetch);
  const offer = model !== undefined && modelBlockFits(draft, model, TETRAD_ROUND_TYPES);
  const was = draft.focus ?? PROGRAM_FOCUS[0];
  const [focus, setFocus] = useState<ProgramFocus>(was);
  const change = useMemo(
    () => (offer && model && focus !== was ? { model, focus } : undefined),
    [offer, model, focus, was],
  );
  const next = useMemo(() => nextBlock(draft, change), [nextBlock, draft, change]);
  const from = programRoundLabel(first + 1);
  const to = programRoundLabel(first + NEXT_BLOCK_ROUNDS);
  const slots = next.ok ? (next.draft.sessions[sIdx]?.slots ?? []).slice(0, PREVIEW_ROWS) : [];
  return (
    <PageSheet
      theme={theme}
      visible
      size="compact"
      onRequestClose={onClose}
      title={t("editorV3.rule.blockTitle", { from, to, defaultValue: "Add {{from}}-{{to}}" })}
      cancelLabel={t("program.cancel", { defaultValue: "Cancel" })}
      action={{
        label: t("editorV3.rule.blockAdd", { count: NEXT_BLOCK_ROUNDS, defaultValue: "Add {{count}} rounds" }),
        disabled: !next.ok,
        onPress: () => {
          onAdd(change);
          onClose();
        },
      }}
    >
      <View style={local.body}>
        {next.ok ? null : (
          // «Add» is off: say why, first.
          <Text style={styles.muted}>
            {t("editorV3.rule.blockFull", {
              max: MAX_CYCLE_WEEKS,
              defaultValue: "A program holds at most {{max}} rounds: there is no room for another tetrad.",
            })}
          </Text>
        )}
        <Text style={styles.muted}>
          {t("editorV3.rule.blockShape", {
            defaultValue: "One more tetrad: two normal rounds, one test round, one deload round.",
          })}
        </Text>
        {offer || loading ? (
          <ModelFocusRow theme={theme} focus={focus} onSelect={setFocus} hintStyle={styles.muted} reserve={!offer} />
        ) : null}
        {change ? (
          <Text style={styles.muted}>
            {t("editorV3.rule.blockFocus", {
              defaultValue: "The new rounds follow the model at this focus. The rounds before stay as they are.",
            })}
          </Text>
        ) : (
          <>
            <Text style={styles.muted}>
              {t("editorV3.rule.blockCarry", {
                defaultValue: "Load and rep rows carry on. Reps in a range start again; a written load goes up one step.",
              })}
            </Text>
            <Text style={styles.muted}>
              {t("editorV3.rule.blockEffort", {
                defaultValue: "Effort rows repeat their shape. The load rises with the MAX measured in the test round.",
              })}
            </Text>
          </>
        )}
        {slots.length > 0 ? (
          <View style={styles.ladder}>
            {slots.map((s) => (
              <View key={s.id} style={[styles.ladderRow, local.row, stacked ? local.stacked : null]} accessible>
                <Text style={[styles.lensNameText, stacked ? null : local.name]} numberOfLines={2}>
                  {resolveName(s.exerciseId, s.exerciseName)}
                </Text>
                <View style={local.round}>
                  <Text style={styles.roundLineInline}>{from}</Text>
                  {/* One line per set group: a narrow sheet never breaks after a «·». */}
                  <View style={local.parts}>
                    {(formatDraftWeekParts(s, first, lineOptions) ?? [
                      t("editorV3.notInRound", { defaultValue: "Not in this round" }),
                    ]).map((part, i) => (
                      <Text key={i} style={styles.roundLineInline}>
                        {part}
                      </Text>
                    ))}
                  </View>
                </View>
              </View>
            ))}
          </View>
        ) : null}
      </View>
    </PageSheet>
  );
}

function makeStyles(theme: UiTheme) {
  const s = theme.spacing;
  return StyleSheet.create({
    body: { padding: s.s5, gap: s.s3 },
    row: { paddingVertical: s.s2 },
    stacked: { flexDirection: "column", alignItems: "flex-start", gap: s.s1 },
    name: { flex: 1 },
    round: { flexDirection: "row", gap: s.s2, flexShrink: 1 },
    parts: { flexShrink: 1 },
  });
}
